import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { Modal } from "react-bootstrap";
import { useEngagementContract } from "./useEngagementContract";
import ContractChoiceScreen from "./ContractChoiceScreen";
import { ContractEditor, ContractList, ContractFormModal } from "src/modules/contracts";
import useOrganizationContracts from "src/modules/organizations/contracts/hooks/useOrganizationContracts";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { CreateContractPayload } from "@services/finance/contracts/commands/cmdCreateContract";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import { Paths } from "src/paths";
import { useCurrentPath, withReturnTo } from "src/shared/utils/returnTo";

export function EngagementContractTab() {
  const navigate = useNavigate();
  const here = useCurrentPath();
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement?.id;
  const clientOrgId = selectedEngagement?.client?.id ?? 0;
  const clientName = selectedEngagement?.client?.name || "this client";

  // Assignment side: which contract belongs to this engagement, and what it
  // could be attached to (scoped to this client and its service provider).
  const {
    availableContracts,
    assignedDetail,
    isLoading: isLoadingAssignment,
    isSaving: isSavingAssignment,
    error: assignmentError,
    assignContract,
    removeContract,
  } = useEngagementContract(engagementId);

  // Editing side: the same store the organization page writes through, so
  // edits made here show up there and vice versa.
  const org = useOrganizationContracts(clientOrgId);

  const [showPicker, setShowPicker] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDetach, setConfirmDetach] = useState(false);

  const assignedContractId = assignedDetail?.contract.id ?? null;

  // Point the editing store at the attached contract so its budgets,
  // pricesheets, and spend load for the right one.
  useEffect(() => {
    if (assignedContractId && org.selectedContractId !== assignedContractId) {
      org.selectContract(assignedContractId);
    }
  }, [assignedContractId, org.selectedContractId]);

  const contract = org.contracts.find((c) => c.id === assignedContractId) ?? assignedDetail?.contract;

  const attach = async (contractId: number) => {
    await assignContract(contractId);
    setShowPicker(false);
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
  };

  const handleCreate = async (payload: CreateContractPayload | UpdateContractPayload) => {
    const created = await org.createContract(payload as CreateContractPayload);
    setShowCreate(false);
    if (created) await attach(created.id);
  };

  const handleDetach = async () => {
    await removeContract();
    setConfirmDetach(false);
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
  };

  const isBusy = isSavingAssignment || org.isSaving;

  if (isLoadingAssignment) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5 text-muted gap-2">
        <span className="spinner-border spinner-border-sm" role="status" />
        <span style={{ fontSize: "0.9rem" }}>Loading contract...</span>
      </div>
    );
  }

  const error = assignmentError || org.error;

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {!contract ? (
        <ContractChoiceScreen
          clientName={clientName}
          existingCount={availableContracts.length}
          isBusy={isBusy}
          onCreateNew={() => setShowCreate(true)}
          onBrowseExisting={() => setShowPicker(true)}
        />
      ) : (
        <ContractEditor
          contract={contract}
          budgets={org.budgets}
          pricesheets={org.pricesheets}
          pricesheetItems={org.pricesheetItems}
          spendAccount={org.spendAccount}
          credits={org.credits}
          debits={org.debits}
          balance={org.balance}
          organizationId={clientOrgId}
          isSaving={isBusy}
          title={contract.name?.trim() || `Contract #${contract.id}`}
          mutations={{
            updateContract: org.updateContract,
            deleteContract: org.deleteContract,
            createBudget: org.createBudget,
            updateBudget: org.updateBudget,
            deleteBudget: org.deleteBudget,
            savePricesheet: org.savePricesheet,
            deletePricesheet: org.deletePricesheet,
            createCredit: org.createCredit,
            updateCredit: org.updateCredit,
            deleteCredit: org.deleteCredit,
            createDebit: org.createDebit,
            updateDebit: org.updateDebit,
            deleteDebit: org.deleteDebit,
          }}
          statusSlot={<span className="contract-status-badge assigned">Attached</span>}
          onEditPricesheet={(p) =>
            navigate(
              withReturnTo(
                `${import.meta.env.BASE_URL}${Paths.CONTRACT_PRICESHEET}`
                  .replace(":contractId", String(contract.id))
                  .replace(":pricesheetId", String(p.id)),
                here,
              ),
            )
          }
          onContractDeleted={handleDetach}
          actionsSlot={
            !confirmDetach ? (
              <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.72rem" }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowPicker(true)}
                  disabled={isBusy}
                  style={{ fontSize: "0.72rem" }}
                >
                  Change contract
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setConfirmDetach(true)}
                  disabled={isBusy}
                  style={{ fontSize: "0.72rem" }}
                >
                  Detach
                </button>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <span className="text-warning" style={{ fontSize: "0.78rem" }}>
                  Detach from this engagement?
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={handleDetach}
                  disabled={isBusy}
                  style={{ fontSize: "0.72rem" }}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setConfirmDetach(false)}
                  disabled={isBusy}
                  style={{ fontSize: "0.72rem" }}
                >
                  Cancel
                </button>
              </div>
            )
          }
        />
      )}

      <Modal show={showPicker} onHide={() => setShowPicker(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Select a contract</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContractList
            contracts={availableContracts}
            selectedId={assignedContractId}
            onSelect={attach}
            title="Available contracts"
            isBusy={isBusy}
            emptyMessage="No contracts exist between this client and service provider"
          />
        </Modal.Body>
      </Modal>

      {showCreate && (
        <ContractFormModal
          show
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
          contract={null}
          clientOrgId={clientOrgId}
          lockedServiceProviderOrgId={selectedEngagement?.service_provider?.id ?? null}
          isSaving={isBusy}
        />
      )}
    </>
  );
}
